import React from 'react';
import { Eye, Heart, Home, Users, Building2, Sprout } from 'lucide-react';
import { Card } from '@/components/ui/card';
import Seo from '@/components/Seo';
import Reveal from '@/components/Reveal';
import PageHero from '@/components/PageHero';

const principles = [
  { title: 'Vision', icon: <Eye className="w-8 h-8" />, description: 'Creating homes where every individual is seen, valued, and supported on their unique journey.' },
  { title: 'Regulation', icon: <Heart className="w-8 h-8" />, description: 'Providing emotionally regulated environments that foster safety, stability, and growth.' },
  { title: 'Stability', icon: <Home className="w-8 h-8" />, description: 'Building consistent, structured support that individuals can rely on every day.' },
  { title: 'Belonging', icon: <Users className="w-8 h-8" />, description: 'Creating a true sense of home where individuals feel they genuinely belong.' }
];

const About = () => {
  return (
    <div className="relative z-10 min-h-screen">
      <Seo title="About" description="Forever Path Living™ is a developing, relationship-centered supportive living organization in Virginia for adults with intellectual and developmental disabilities and co-occurring behavioral or mental health support needs." />
      <PageHero
        eyebrow="Our Story"
        title="About Forever Path Living™"
        subtitle="A relationship-centered organization built on dignity, emotional safety, trust, and belonging."
      />

      {/* Intro */}
      <section className="py-20 md:py-28 px-6 bg-cream-100">
        <div className="max-w-4xl mx-auto space-y-10">
          <Reveal>
            <p className="text-lg md:text-xl text-forest-700 leading-relaxed">
              Forever Path Living™ is a developing network of supportive living homes designed for adults with
              intellectual and developmental disabilities, including individuals with co-occurring behavioral or
              mental health support needs, also known as dual diagnosis.
            </p>
          </Reveal>

          <Reveal className="bg-cream-200 rounded-3xl p-8 md:p-12 border border-sage-200">
            <p className="text-lg text-forest-700 leading-relaxed mb-6">
              The program focuses on individuals who may have experienced placement instability due to environments
              where appropriate supports were not always in place.
            </p>
            <p className="text-lg text-forest-700 leading-relaxed mb-6">
              Forever Path Living™ provides emotionally regulated, structured environments where individuals are
              supported with consistency, patience, and respect.
            </p>
            <p className="text-lg text-forest-900 font-semibold leading-relaxed">
              Each individual is approached as a person first — not as a diagnosis or a history.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Population We Serve */}
      <section className="py-20 md:py-28 px-6 bg-sage-100">
        <Reveal className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sage-600 mb-4">Population We Serve</p>
          <p className="text-xl md:text-2xl text-forest-800 leading-relaxed mb-6 font-serif">
            Forever Path Living™ is being developed to support adults with intellectual and developmental disabilities
            and co-occurring behavioral health and/or mental health support needs.
          </p>
          <p className="text-lg text-forest-700 leading-relaxed">
            Support is individualized and provided within the scope of our licensure, staffing, training, safety
            practices, and clinical appropriateness.
          </p>
        </Reveal>
      </section>

      {/* Philosophy */}
      <section className="py-20 md:py-28 px-6 bg-cream-100">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-5xl text-forest-800">Our Philosophy</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 70}>
                <Card className="h-full p-8 md:p-10 bg-cream-200 border border-sage-200 rounded-3xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(59,77,65,0.08)]">
                  <div className="flex flex-col items-center text-center gap-5">
                    <div className="p-5 rounded-2xl bg-sage-100 text-forest-700">{p.icon}</div>
                    <h3 className="font-serif text-2xl text-forest-800">{p.title}</h3>
                    <p className="text-forest-700 leading-relaxed">{p.description}</p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Organization vs Home */}
      <section className="py-20 md:py-28 px-6 bg-cream-200">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <Reveal>
            <Card className="h-full p-8 md:p-10 bg-forest-900 text-sage-100 rounded-3xl border border-forest-800">
              <div className="inline-flex p-3 rounded-2xl bg-forest-800 text-gold-500 mb-5"><Building2 className="w-7 h-7" /></div>
              <h3 className="font-serif text-2xl text-cream-100 mb-3">Forever Path Living™</h3>
              <p className="text-sm uppercase tracking-widest text-gold-500 mb-4">The Organization</p>
              <p className="leading-relaxed text-sage-100/85">
                Forever Path Living™ is not a female-only organization. Our organization intends to grow thoughtfully
                over time in response to community needs, organizational readiness, available resources, and future
                licensing.
              </p>
            </Card>
          </Reveal>
          <Reveal delay={80}>
            <Card className="h-full p-8 md:p-10 bg-cream-100 rounded-3xl border border-sage-200">
              <div className="inline-flex p-3 rounded-2xl bg-sage-100 text-forest-700 mb-5"><Home className="w-7 h-7" /></div>
              <h3 className="font-serif text-2xl text-forest-800 mb-3">The Chasnell Home™</h3>
              <p className="text-sm uppercase tracking-widest text-gold-700 mb-4">Our First Residential Home</p>
              <p className="leading-relaxed text-forest-700">
                The Chasnell Home™ is our initial residential program and is currently being developed to serve adult
                females as part of our initial Virginia DBHDS licensure strategy. This reflects our current operational
                plan — not the limits of our long-term vision.
              </p>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* Growth / closing */}
      <section className="py-20 md:py-28 px-6 bg-sage-100">
        <Reveal className="max-w-4xl mx-auto text-center">
          <div className="inline-flex p-4 rounded-2xl bg-cream-100 text-forest-700 mb-8"><Sprout className="w-8 h-8" /></div>
          <p className="font-serif text-2xl md:text-3xl text-forest-800 italic leading-relaxed">
            Because Every Story Deserves a Forever Path™
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
